/**
 * CreditConfirmModal — Xác nhận credits trước khi dịch
 *
 * 2 trường hợp:
 * 1. Đủ credits → hiện preview + confirm
 * 2. Không đủ → hiện upsell → navigate tới CreditShop
 */

import React from 'react';
import { FileText, AlertTriangle, ShoppingCart, X, Loader2, Zap, ShieldCheck } from 'lucide-react';
import { formatCredits } from '../../services/mock/creditMock';
import type { CreditCheck } from '../../types/credit';

interface CreditConfirmModalProps {
  isOpen: boolean;
  fileName: string;
  creditCheck: CreditCheck;
  onConfirm: () => void;
  onCancel: () => void;
  onBuyCredits: () => void;
  /** Hiện toggle "Kèm kiểm tra chất lượng" (chỉ cho mode translate). */
  showQCToggle?: boolean;
  /** Trạng thái QC toggle hiện tại. */
  includeQC?: boolean;
  /** Callback khi user toggle QC (parent update file.skipQC). */
  onToggleQC?: () => void;
  /** Estimate credits cho QC nếu toggle ON (optional, hiện inline khi có). */
  qcCredits?: number | null;
  /** Mode action — đổi title/icon/button text cho phù hợp. Default 'translate'. */
  mode?: 'translate' | 'retranslate' | 'qc' | 'audit' | 'train';
}

const MODE_CONFIG: Record<NonNullable<CreditConfirmModalProps['mode']>, { title: string; confirm: string; Icon: typeof Zap }> = {
  translate: { title: 'Xác nhận dịch', confirm: 'Dịch ngay', Icon: Zap },
  retranslate: { title: 'Xác nhận dịch lại', confirm: 'Dịch lại', Icon: Zap },
  qc: { title: 'Xác nhận kiểm tra chất lượng', confirm: 'Kiểm tra ngay', Icon: ShieldCheck },
  audit: { title: 'Xác nhận thẩm định', confirm: 'Thẩm định ngay', Icon: ShieldCheck },
  train: { title: 'Xác nhận huấn luyện', confirm: 'Huấn luyện ngay', Icon: Zap },
};

const CreditConfirmModal: React.FC<CreditConfirmModalProps> = ({
  isOpen,
  fileName,
  creditCheck,
  onConfirm,
  onCancel,
  onBuyCredits,
  showQCToggle = false,
  includeQC = false,
  onToggleQC,
  qcCredits = null,
  mode = 'translate',
}) => {
  if (!isOpen) return null;

  const { allowed, creditsNeeded, creditsAvailable, creditsAfter, isComputing } = creditCheck;
  const cfg = MODE_CONFIG[mode];
  const ConfirmIcon = cfg.Icon;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-white/60">
        {/* Header — gradient brand emerald */}
        <div className="relative overflow-hidden">
          <div className={`absolute inset-0 ${
            isComputing
              ? 'bg-gradient-to-br from-slate-50 to-white'
              : allowed
                ? 'bg-gradient-to-br from-emerald-50 via-white to-teal-50/40'
                : 'bg-gradient-to-br from-red-50 via-white to-amber-50/40'
          }`} />
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-emerald-200/30 rounded-full blur-3xl pointer-events-none" />

          <div className="relative px-6 py-5 flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-lg shrink-0 ${
                isComputing
                  ? 'bg-slate-200 shadow-slate-200/50'
                  : allowed
                    ? 'bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-500/30'
                    : 'bg-gradient-to-br from-red-500 to-amber-500 shadow-red-500/20'
              }`}>
                {isComputing ? (
                  <Loader2 className="w-5 h-5 text-slate-500 animate-spin" />
                ) : allowed ? (
                  <ConfirmIcon className={`w-5 h-5 text-white ${ConfirmIcon === Zap ? 'fill-current' : ''}`} />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-black text-slate-900 tracking-tight">
                  {isComputing ? 'Đang tính toán credits...' : allowed ? cfg.title : 'Không đủ credits'}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5 truncate" title={fileName}>
                  {fileName}
                </p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-white/70 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {isComputing ? (
            <div className="py-8 flex flex-col items-center gap-3 text-sm text-slate-500">
              <Loader2 className="w-7 h-7 animate-spin text-emerald-500" />
              <p>Tính toán chi phí qua Gemini API...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* QC toggle (chỉ hiện khi mode=translate + showQCToggle) */}
              {showQCToggle && allowed && onToggleQC && (
                <button
                  onClick={onToggleQC}
                  className={`w-full flex items-start gap-3 p-3 rounded-xl border transition-all text-left ${
                    includeQC
                      ? 'bg-violet-50 border-violet-300 hover:bg-violet-100'
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${
                    includeQC
                      ? 'bg-violet-600 border-violet-600'
                      : 'bg-white border-slate-300'
                  }`}>
                    {includeQC && <span className="text-white text-[10px] font-black leading-none">✓</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-sm font-bold text-slate-800">
                      <ShieldCheck className={`w-4 h-4 ${includeQC ? 'text-violet-600' : 'text-slate-400'}`} />
                      Kèm kiểm tra chất lượng
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                      AI đánh giá bản dịch theo 5 tiêu chí (chính xác, thuật ngữ, mượt, đầy đủ, văn phong).
                      {includeQC && qcCredits != null && (
                        <span className="ml-1 font-semibold text-violet-700 tabular-nums">
                          +{formatCredits(qcCredits)} credits
                        </span>
                      )}
                    </p>
                  </div>
                </button>
              )}

              {/* Credits summary */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-2.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Credits cần</span>
                  <span className="font-black tabular-nums text-slate-900">
                    {formatCredits(creditsNeeded)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Bạn đang có</span>
                  <span className={`font-black tabular-nums ${allowed ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCredits(creditsAvailable)}
                  </span>
                </div>
                {allowed && (
                  <>
                    <div className="border-t border-slate-200" />
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Còn lại sau khi chạy</span>
                      <span className="font-black tabular-nums text-slate-700">
                        {formatCredits(creditsAfter)}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {!allowed && (
                <div className="p-3 bg-red-50 rounded-xl text-sm text-red-700 border border-red-200">
                  <p className="font-semibold mb-1">Thiếu {formatCredits(creditsNeeded - creditsAvailable)} credits</p>
                  <p className="text-xs text-red-600/80">Mua thêm gói credits để tiếp tục dịch tài liệu.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50/70 border-t border-slate-100 flex gap-2">
          {isComputing ? (
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:bg-white transition-colors"
            >
              Hủy
            </button>
          ) : allowed ? (
            <>
              <button
                onClick={onCancel}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:bg-white transition-colors"
              >
                Hủy
              </button>
              <button
                onClick={onConfirm}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.98]"
              >
                <ConfirmIcon className={`w-4 h-4 ${ConfirmIcon === Zap ? 'fill-current' : ''}`} />
                {cfg.confirm}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onCancel}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:bg-white transition-colors"
              >
                Hủy
              </button>
              <button
                onClick={onBuyCredits}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.98]"
              >
                <ShoppingCart className="w-4 h-4" />
                Mua thêm credits
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditConfirmModal;
